/**
 * The one hook every report fetches through — CLAUDE.md §4.7. No bare `fetch` in a component, no
 * `useEffect` data loading, no hand-rolled loading flags.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  queryReport,
  type ReportQueryRequest,
  type ReportQueryResponse,
} from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import type { SupportedLocale } from '@/lib/locale';

export interface UseReportQueryOptions {
  /**
   * Which governed view of the report to run (§5.1). Part of the request body and so of the cache
   * key, which is what stops two views of one report serving each other's rows.
   */
  view?: string;
  /** Hold the query back — typically `filters.isComplete`, so a required date range is chosen first. */
  enabled?: boolean;
  /**
   * How long a result stays fresh, in ms. Roughly the source sync cadence (§6.3) — refetching
   * faster adds load without freshness. Omit for the default in `lib/query.ts`.
   */
  staleTime?: number;
  /*
   * There is deliberately no `sort` option (§4.3, DECISIONS.md §30, owner decision 2026-08-06).
   * `/query` no longer carries one: the browser orders the rows, and the cache key is built from
   * the request body, so a sort here would make every header click a new key and re-query — the
   * cost the rule was changed to remove. Reports keep their sort for `<DataTable>` and for the
   * export request, which the server still orders. Do not add it back without reading §30.
   */
  /** Paginate deliberately, not reflexively — see §5.1 before reaching for this. */
  page?: { offset: number; limit: number };
  /** Poll interval in ms, for a screen left open on a floor. Never faster than the sync cadence (§6.3). */
  refetchInterval?: number;
  /**
   * Keep polling when the tab is in the background. Default false (TanStack Query). Wall boards
   * (floor / factory) set true so a kiosk or unfocused display does not freeze mid-shift.
   */
  refetchIntervalInBackground?: boolean;
}

/**
 * The query key, carrying the full filter object so no filter can serve another's rows (§4.7).
 * Exported so a mutation can invalidate by id — `['report', reportId]` prefixes every entry.
 */
export function reportQueryKey(
  reportId: string,
  locale: SupportedLocale,
  body: ReportQueryRequest,
): readonly [string, string, SupportedLocale, ReportQueryRequest] {
  return ['report', reportId, locale, body];
}

export function useReportQuery<TRow = Record<string, unknown>>(
  reportId: string,
  filters: ReportQueryRequest['filters'],
  options: UseReportQueryOptions = {},
): UseQueryResult<ReportQueryResponse<TRow>, Error> {
  const { locale } = useI18n();
  const {
    enabled = true,
    staleTime,
    page,
    view,
    refetchInterval,
    refetchIntervalInBackground,
  } = options;

  // Sort is deliberately absent (§4.3, DECISIONS.md §30): the browser orders the rows, and the key
  // is built from this body — leaving sort in it would make every header click a new key, which is
  // exactly the refetch the rule was changed to remove. `sort` still reaches the export request and
  // `<DataTable>`, both of which the caller builds itself.
  // Spread conditionally: an explicit `staleTime: undefined` would overwrite lib/query.ts's default.
  const body: ReportQueryRequest = {
    ...(view !== undefined ? { view } : {}),
    filters,
    ...(page !== undefined ? { page } : {}),
  };

  return useQuery({
    queryKey: reportQueryKey(reportId, locale, body),
    queryFn: () => queryReport<TRow>(reportId, body, locale),
    enabled,
    ...(staleTime !== undefined ? { staleTime } : {}),
    ...(refetchInterval !== undefined ? { refetchInterval } : {}),
    ...(refetchIntervalInBackground !== undefined
      ? { refetchIntervalInBackground }
      : {}),
  });
}
